import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  CalendarMonth,
  CheckCircle,
  EventSeat,
  Groups,
  LocationOn,
  PersonAddAlt,
} from "@mui/icons-material";

import "../styles/community-meetups.css";
import { buildRichHtmlMarkup, getPlainTextFromHtml } from "../utils/richHtml";

const defaultInterestOptions = [
  "Second income / entrepreneurship",
  "Student internship / mentor",
  "Immigration help: O-1 / NIW / EB-1A",
  "Business or enterprise partnership",
  "Finance / insurance / family planning",
  "Healthcare / Ayurveda / wellness",
  "Education / art / music / tutoring",
  "Food / catering / baking",
  "Priest / ritual services",
  "Volunteer or community leader",
  "Not sure yet",
];

const emptyForm = {
  slotId: "",
  fullName: "",
  email: "",
  phone: "",
  cityZip: "",
  seatsRequested: 1,
  primaryInterest: "",
  customAnswers: {},
  notes: "",
  consent: false,
  website: "",
};

const buildEventImageUrl = (imageId) =>
  `${process.env.REACT_APP_API_BASE_URL}/api/admin/events/images/${imageId}`;

const parseEventId = (value = "") => {
  const cleaned = String(value).replace(/^event-/i, "");
  const number = Number(cleaned);
  return Number.isInteger(number) && number > 0 ? number : "";
};

const toEventDateObject = (event) => {
  if (!event?.eventDate) return null;
  const datePart = String(event.eventDate).slice(0, 10);
  const timePart = String(event.startTime || "00:00").slice(0, 5);
  const date = new Date(`${datePart}T${timePart}`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatEventDate = (event) => {
  const date = toEventDateObject(event);
  if (!date) return "Date to be announced";
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const formatEventTime = (event) => {
  const start = toEventDateObject(event);
  const datePart = String(event?.eventDate || "").slice(0, 10);
  const end = event?.endTime ? new Date(`${datePart}T${String(event.endTime).slice(0, 5)}`) : null;
  const formatter = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  if (!start) return "Time to be announced";
  return end && !Number.isNaN(end.getTime())
    ? `${formatter.format(start)} – ${formatter.format(end)}`
    : formatter.format(start);
};

const toGoogleCalendarDate = (date) =>
  date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");

const buildGoogleCalendarLink = (event) => {
  const start = toEventDateObject(event);
  if (!start) return "";
  const datePart = String(event?.eventDate || "").slice(0, 10);
  const end = event?.endTime
    ? new Date(`${datePart}T${String(event.endTime).slice(0, 5)}`)
    : new Date(start.getTime() + 60 * 60 * 1000);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.eventTitle || "Udyog Connect Event",
    dates: `${toGoogleCalendarDate(start)}/${toGoogleCalendarDate(end)}`,
    details: getPlainTextFromHtml(event.description) || event.eventTitle || "",
    location: event.venue || "",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const buildInitialCustomAnswers = (fields = []) =>
  fields.reduce((answers, field) => {
    answers[field.key] = field.type === "checkbox" ? false : "";
    return answers;
  }, {});

const EventRegistrationPage = () => {
  const { eventKey } = useParams();
  const navigate = useNavigate();
  const eventId = useMemo(() => parseEventId(eventKey), [eventKey]);
  const [event, setEvent] = useState(null);
  const [images, setImages] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [submitState, setSubmitState] = useState({ status: "idle", message: "" });

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setSubmitState({ status: "idle", message: "" });

    if (!eventId) {
      setLoading(false);
      setSubmitState({
        status: "error",
        message: "This UC event registration link is not valid.",
      });
      return () => {
        mounted = false;
      };
    }

    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/events/${encodeURIComponent(eventId)}`)
      .then(async (response) => {
        if (!mounted) return;
        const loadedEvent = response.data?.event;
        setEvent(loadedEvent || null);
        setForm({
          ...emptyForm,
          customAnswers: buildInitialCustomAnswers(loadedEvent?.registrationFields || []),
        });

        try {
          const imageRes = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/admin/events/${eventId}/images`
          );
          if (!mounted) return;
          setImages(
            (Array.isArray(imageRes.data) ? imageRes.data : []).map((image) => ({
              id: image.id,
              url: buildEventImageUrl(image.id),
            }))
          );
        } catch (error) {
          console.warn("Unable to load event registration images", eventId, error);
          if (mounted) setImages([]);
        }
      })
      .catch((error) => {
        if (!mounted) return;
        setSubmitState({
          status: "error",
          message: error?.response?.data?.message || "This UC event registration link is not available.",
        });
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [eventId]);

  const seatsText = useMemo(() => {
    if (!event?.capacity) return "Open registration";
    const remaining = Math.max(0, Number(event.seatsLeft ?? event.capacity));
    return `${remaining} of ${event.capacity} seats left`;
  }, [event]);

  const selectedSlot = useMemo(
    () => (event?.slots || []).find((slot) => Number(slot.id) === Number(form.slotId)) || null,
    [event, form.slotId]
  );

  const interestSettings = useMemo(() => ({
    enabled: event?.primaryInterestSettings?.enabled ?? event?.showPrimaryInterest !== false,
    label: event?.primaryInterestSettings?.label || "Main interest",
    required: Boolean(event?.primaryInterestSettings?.required),
    placeholder: event?.primaryInterestSettings?.placeholder || "Choose one (optional)",
    options: event?.primaryInterestSettings?.options?.length
      ? event.primaryInterestSettings.options
      : defaultInterestOptions,
  }), [event]);

  const seatOptions = useMemo(() => {
    const availableSeats = event?.slotRegistrationEnabled ? selectedSlot?.seatsLeft : event?.seatsLeft;
    const hasCapacity = event?.slotRegistrationEnabled ? Boolean(selectedSlot) : Boolean(event?.capacity);
    const maxSeats = hasCapacity ? Math.max(0, Math.min(10, Number(availableSeats || 0))) : 10;
    return Array.from({ length: Math.max(1, maxSeats) }, (_, index) => index + 1);
  }, [event, selectedSlot]);

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const updateCustomAnswer = (key, value) => {
    setForm((current) => ({
      ...current,
      customAnswers: {
        ...(current.customAnswers || {}),
        [key]: value,
      },
    }));
  };

  const submitRegistration = async (submitEvent) => {
    submitEvent.preventDefault();
    if (!event?.id) return;

    if (!form.fullName.trim() || !form.email.trim() || !form.consent) {
      setSubmitState({
        status: "error",
        message: "Please enter your name, email, and consent before registering.",
      });
      return;
    }
    if (event.slotRegistrationEnabled && !selectedSlot) {
      setSubmitState({ status: "error", message: "Please select an available time slot." });
      return;
    }
    if (interestSettings.enabled && interestSettings.required && !form.primaryInterest) {
      setSubmitState({ status: "error", message: `${interestSettings.label} is required.` });
      return;
    }

    setSubmitState({ status: "submitting", message: "Registering you for this UC event…" });
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/events/register`, {
        ...form,
        eventId: event.id,
        seatsRequested: Number(form.seatsRequested) || 1,
        customAnswers: form.customAnswers,
        source: "uc_event_registration_page",
      });
      setSubmitState({
        status: "success",
        message: response.data?.message || "You are registered for this UC event.",
      });
      setEvent((current) => {
        if (!current) return current;
        const nextSeats = Number(form.seatsRequested) || 1;
        return {
          ...current,
          registeredSeats: Number(current.registeredSeats || 0) + nextSeats,
          seatsLeft: current.capacity ? Math.max(0, Number(current.seatsLeft || 0) - nextSeats) : null,
          slots: (current.slots || []).map((slot) => Number(slot.id) === Number(form.slotId)
            ? { ...slot, registeredSeats: Number(slot.registeredSeats || 0) + nextSeats, seatsLeft: Math.max(0, Number(slot.seatsLeft || 0) - nextSeats) }
            : slot),
        };
      });
    } catch (error) {
      setSubmitState({
        status: "error",
        message: error?.response?.data?.message || "Unable to register right now.",
      });
    }
  };

  if (loading) {
    return (
      <section className="uc-meetup-registration-page uc-event-registration-page">
        <div className="uc-meetup-registration-card loading">Loading event registration…</div>
      </section>
    );
  }

  if (!event) {
    return (
      <section className="uc-meetup-registration-page uc-event-registration-page">
        <div className="uc-meetup-registration-card">
          <span className="uc-meetups-kicker">
            <CalendarMonth fontSize="inherit" />
            Udyog Connect Event
          </span>
          <h1>Registration link not available</h1>
          <p>{submitState.message || "Please choose another upcoming UC event."}</p>
          <Link className="uc-meetup-home-link" to="/2.0/calendar">
            View upcoming events
          </Link>
        </div>
      </section>
    );
  }

  const calendarEvent = selectedSlot
    ? { ...event, eventDate: selectedSlot.slotDate, startTime: selectedSlot.startTime, endTime: selectedSlot.endTime }
    : event;
  const calendarLink = buildGoogleCalendarLink(calendarEvent);
  const summary = getPlainTextFromHtml(event.description);
  const compactSummary = (() => {
    const normalized = String(summary || "").replace(/\s+/g, " ").trim();
    if (!normalized) return "Register for this Udyog Connect community event.";
    if (normalized.length <= 180) return normalized;
    const shortened = normalized.slice(0, 180).replace(/\s+\S*$/, "").trim();
    return `${shortened || normalized.slice(0, 180)}…`;
  })();
  const paymentSettings = event.paymentSettings || {};
  const isPaidEvent = Boolean(paymentSettings.enabled && Number(paymentSettings.amountUsd) > 0);
  const estimatedTotal = isPaidEvent
    ? Math.round(Number(paymentSettings.amountUsd) * Number(form.seatsRequested || 1) * 100) / 100
    : 0;
  const registrationClosed =
    !event.nativeRegistrationEnabled ||
    String(event.status || "").toLowerCase() !== "open" ||
    (event.capacity && Number(event.seatsLeft || 0) <= 0);
  const selectedSchedule = selectedSlot
    ? { eventDate: selectedSlot.slotDate, startTime: selectedSlot.startTime, endTime: selectedSlot.endTime }
    : event;

  return (
    <section className="uc-meetup-registration-page uc-event-registration-page">
      <div className="uc-meetup-registration-hero uc-event-registration-hero">
        <div className="uc-event-hero-copy">
          <span className="uc-meetups-kicker">
            <CalendarMonth fontSize="inherit" />
            {event.eventCategory || "Udyog Connect Event"}
          </span>
          <h1>{event.eventTitle || "Udyog Connect Event"}</h1>
          <p className="uc-event-hero-summary">{compactSummary}</p>
          <div className="uc-event-hero-meta" aria-label="Event schedule and venue">
            <span>
              <CalendarMonth fontSize="small" />
              <span>
                <small>Date</small>
                <strong>{formatEventDate(selectedSchedule)}</strong>
              </span>
            </span>
            <span>
              <Groups fontSize="small" />
              <span>
                <small>Time</small>
                <strong>{formatEventTime(selectedSchedule)}</strong>
              </span>
            </span>
            <span>
              <LocationOn fontSize="small" />
              <span>
                <small>Venue</small>
                <strong>{event.venue || "Venue to be shared"}</strong>
              </span>
            </span>
          </div>
          <div className="uc-event-hero-actions">
            {registrationClosed ? (
              <span className="uc-event-hero-cta disabled">Registration closed</span>
            ) : (
              <a className="uc-event-hero-cta" href="#uc-event-registration-form">
                <PersonAddAlt fontSize="small" />
                Register now
              </a>
            )}
            <span className="uc-event-hero-availability">
              <EventSeat fontSize="small" />
              {seatsText}
            </span>
          </div>
        </div>
        <div className="uc-event-hero-side">
          {images.length > 0 && (
            <div className={`uc-event-hero-photos count-${Math.min(images.length, 4)}`} aria-label="Event photos">
              {images.slice(0, 4).map((image) => (
                <img key={image.id} src={image.url} alt={event.eventTitle || "Udyog Connect event"} />
              ))}
            </div>
          )}
          {isPaidEvent && (
            <div className="uc-event-payment-card">
              <span>Payment-ready event</span>
              <strong>${Number(paymentSettings.amountUsd).toFixed(2)}</strong>
              <small>{paymentSettings.label || "Event registration"} · per seat</small>
            </div>
          )}
        </div>
      </div>

      <div className="uc-event-rich-content-card">
        <span className="uc-meetups-kicker">Event details</span>
        <div className="uc-rich-html" dangerouslySetInnerHTML={buildRichHtmlMarkup(event.description)} />
      </div>

      <div className="uc-meetup-registration-layout">
        <aside className="uc-meetup-registration-card info">
          <h2>Event details</h2>
          <div className="uc-meetup-detail-list">
            <span>
              <CalendarMonth fontSize="small" />
              {formatEventDate(selectedSchedule)}
            </span>
            <span>
              <Groups fontSize="small" />
              {formatEventTime(selectedSchedule)}
            </span>
            <span>
              <LocationOn fontSize="small" />
              {event.venue || "Venue to be shared"}
            </span>
            <span>
              <EventSeat fontSize="small" />
              {seatsText}
            </span>
          </div>
          <div className="uc-meetup-location registration">
            <strong>{event.venue || "Venue / meeting details"}</strong>
            <small>UC may follow up with final room, parking, or meeting-link details.</small>
          </div>
          <div className="uc-meetup-tags">
            <span>{event.whoCanApply || "Anyone"}</span>
            <span>{event.status || "Open"}</span>
            {event.externalRegistrationUrl && <span>External link available</span>}
          </div>
          {event.externalRegistrationUrl && (
            <a
              className="uc-event-external-link"
              href={event.externalRegistrationUrl}
              target="_blank"
              rel="noreferrer"
            >
              Open external registration
            </a>
          )}
          {isPaidEvent && (
            <div className="uc-event-cart-card">
              <span>Payment section</span>
              <strong>${estimatedTotal.toFixed(2)}</strong>
              <small>
                {Number(form.seatsRequested || 1)} seat{Number(form.seatsRequested || 1) === 1 ? "" : "s"} · {paymentSettings.label || "Event registration"}
              </small>
              {paymentSettings.addToCartEnabled && (
                <button type="button" disabled title="Checkout integration will be enabled in a later payment phase.">
                  Add to cart coming soon
                </button>
              )}
              <p>{paymentSettings.note || "Payment collection is not enabled yet. UC will follow up if payment is required."}</p>
            </div>
          )}
        </aside>

        <div className="uc-meetup-registration-card" id="uc-event-registration-form">
          {submitState.status === "success" ? (
            <div className="uc-meetup-success">
              <CheckCircle />
              <h2>Registration received</h2>
              <p>{submitState.message}</p>
              <p>
                Please check your email confirmation. UC may follow up with final venue,
                room, or meeting-link details before the event.
              </p>
              <div className="uc-meetup-success-actions">
                {calendarLink && (
                  <a href={calendarLink} target="_blank" rel="noreferrer">
                    Add to Google Calendar
                  </a>
                )}
                <button type="button" onClick={() => navigate("/2.0/udyogconnect-home")}>
                  Go to UC home
                </button>
              </div>
            </div>
          ) : (
            <>
              <h2>
                <PersonAddAlt fontSize="small" />
                Register for this event
              </h2>
              <p className="uc-meetup-registration-note">
                Share your details below so UC can reserve seats and route you to the
                right community lead if follow-up is needed.
              </p>
              <form className="uc-meetup-form" onSubmit={submitRegistration}>
                <label className="uc-meetup-honeypot">
                  Website
                  <input
                    value={form.website}
                    onChange={(formEvent) => updateForm("website", formEvent.target.value)}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </label>

                {event.slotRegistrationEnabled && (
                  <fieldset className="uc-event-slot-picker">
                    <legend>Select a time slot *</legend>
                    <div className="uc-event-slot-options">
                      {(event.slots || []).map((slot) => {
                        const unavailable = slot.status !== "open" || Number(slot.seatsLeft || 0) <= 0;
                        const slotSchedule = { eventDate: slot.slotDate, startTime: slot.startTime, endTime: slot.endTime };
                        return (
                          <label key={slot.id} className={`uc-event-slot-option${Number(form.slotId) === Number(slot.id) ? " selected" : ""}${unavailable ? " unavailable" : ""}`}>
                            <input
                              type="radio"
                              name="eventSlot"
                              value={slot.id}
                              checked={Number(form.slotId) === Number(slot.id)}
                              disabled={unavailable}
                              onChange={(formEvent) => {
                                updateForm("slotId", formEvent.target.value);
                                updateForm("seatsRequested", 1);
                              }}
                              required
                            />
                            <span>
                              <strong>{slot.label}</strong>
                              <small>{formatEventDate(slotSchedule)} · {formatEventTime(slotSchedule)}</small>
                              <small>{unavailable ? "Full / unavailable" : `${slot.seatsLeft} of ${slot.capacity} seats left`}</small>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                )}

                <div className="uc-meetup-form-grid">
                  <label>
                    Full name *
                    <input
                      value={form.fullName}
                      onChange={(formEvent) => updateForm("fullName", formEvent.target.value)}
                      required
                    />
                  </label>
                  <label>
                    Email *
                    <input
                      type="email"
                      value={form.email}
                      onChange={(formEvent) => updateForm("email", formEvent.target.value)}
                      required
                    />
                  </label>
                  <label>
                    Phone
                    <input
                      value={form.phone}
                      onChange={(formEvent) => updateForm("phone", formEvent.target.value)}
                    />
                  </label>
                  <label>
                    City / ZIP
                    <input
                      value={form.cityZip}
                      onChange={(formEvent) => updateForm("cityZip", formEvent.target.value)}
                    />
                  </label>
                  <label>
                    Seats
                    <select
                      value={form.seatsRequested}
                      onChange={(formEvent) => updateForm("seatsRequested", formEvent.target.value)}
                    >
                      {seatOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                {interestSettings.enabled && (
                  <label>
                    {interestSettings.label}{interestSettings.required ? " *" : ""}
                    <select
                      value={form.primaryInterest}
                      required={interestSettings.required}
                      onChange={(formEvent) => updateForm("primaryInterest", formEvent.target.value)}
                    >
                      <option value="">{interestSettings.placeholder}</option>
                      {interestSettings.options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                </div>

                {Array.isArray(event.registrationFields) && event.registrationFields.length > 0 && (
                  <div className="uc-event-custom-fields">
                    <h3>Additional registration details</h3>
                    <div className="uc-meetup-form-grid">
                      {event.registrationFields.map((field) => {
                        const value = form.customAnswers?.[field.key] ?? "";
                        const requiredMark = field.required ? " *" : "";

                        if (field.type === "textarea") {
                          return (
                            <label key={field.key} className="uc-meetup-notes">
                              {field.label}{requiredMark}
                              <textarea
                                value={value}
                                required={field.required}
                                placeholder={field.placeholder || ""}
                                onChange={(formEvent) => updateCustomAnswer(field.key, formEvent.target.value)}
                              />
                            </label>
                          );
                        }

                        if (field.type === "select") {
                          return (
                            <label key={field.key}>
                              {field.label}{requiredMark}
                              <select
                                value={value}
                                required={field.required}
                                onChange={(formEvent) => updateCustomAnswer(field.key, formEvent.target.value)}
                              >
                                <option value="">Choose one</option>
                                {(field.options || []).map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            </label>
                          );
                        }

                        if (field.type === "checkbox") {
                          return (
                            <label key={field.key} className="uc-meetup-consent">
                              <input
                                type="checkbox"
                                checked={Boolean(value)}
                                required={field.required}
                                onChange={(formEvent) => updateCustomAnswer(field.key, formEvent.target.checked)}
                              />
                              <span>{field.label}{requiredMark}</span>
                            </label>
                          );
                        }

                        const inputType =
                          field.type === "phone"
                            ? "tel"
                            : ["email", "number", "date"].includes(field.type)
                              ? field.type
                              : "text";

                        return (
                          <label key={field.key}>
                            {field.label}{requiredMark}
                            <input
                              type={inputType}
                              value={value}
                              required={field.required}
                              placeholder={field.placeholder || ""}
                              onChange={(formEvent) => updateCustomAnswer(field.key, formEvent.target.value)}
                            />
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                <label className="uc-meetup-notes">
                  Optional note
                  <textarea
                    value={form.notes}
                    onChange={(formEvent) => updateForm("notes", formEvent.target.value)}
                    placeholder="Tell us what brought you to this event."
                  />
                </label>

                <label className="uc-meetup-consent">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={(formEvent) => updateForm("consent", formEvent.target.checked)}
                  />
                  <span>
                    I agree UC may contact me about this event and related community
                    opportunities.
                  </span>
                </label>

                {submitState.message && (
                  <div className={`uc-meetup-message ${submitState.status}`}>
                    {submitState.message}
                  </div>
                )}

                <button
                  type="submit"
                  className="uc-meetup-submit"
                  disabled={submitState.status === "submitting" || registrationClosed}
                >
                  {registrationClosed
                    ? "Registration closed"
                    : submitState.status === "submitting"
                      ? "Submitting…"
                      : "Submit registration"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default EventRegistrationPage;
